import SocketFilterMessageTypes from './SocketFilterMessageTypes.js'

// What a connection asks the socket server for, by name. A connection lists
// the channels it wants (at the handshake as `channels=user,match`, or later
// with the `setChannels` event).
// Each channel is one SocketFilterMessageTypes value, the tag PHP's
// Socket_Pusher puts on every push.
export default class SocketChannels {
	static USER = 'user';
	static CHATS = 'chats';
	static MATCH = 'match';
	static TOURNAMENT = 'tournament';
	static STATUS = 'status';
	static LIVE_MATCHES = 'live_matches';
	// The Play page's searching and playing counts.
	static SITE_ACTIVITY = 'site_activity';

	// The channels a logged-out socket (guest=1 at the handshake) may hold.
	static GUEST_CHANNELS = new Set([SocketChannels.LIVE_MATCHES]);

	static MESSAGE_TYPES = Object.freeze({
		[SocketChannels.USER]: SocketFilterMessageTypes.USER_ALWAYS,
		[SocketChannels.CHATS]: SocketFilterMessageTypes.USER_IN_CHATS,
		[SocketChannels.MATCH]: SocketFilterMessageTypes.USER_IN_MATCH,
		[SocketChannels.TOURNAMENT]: SocketFilterMessageTypes.USER_IN_TOURNAMENT,
		[SocketChannels.STATUS]: SocketFilterMessageTypes.SOCKET_UPDATES,
		[SocketChannels.LIVE_MATCHES]: SocketFilterMessageTypes.LIVE_MATCHES,
		[SocketChannels.SITE_ACTIVITY]: SocketFilterMessageTypes.SITE_ACTIVITY,
	});

	/** Whether a parsed channel set is non-empty and holds only guest channels. */
	static allowsGuest(channels) {
		if(!channels || channels.size === 0) {
			return false;
		}
		return Array.from(channels).every((name) => SocketChannels.GUEST_CHANNELS.has(name));
	}

	static isChannel(name) {
		return Object.prototype.hasOwnProperty.call(SocketChannels.MESSAGE_TYPES, name);
	}

	static messageTypeOf(name) {
		return SocketChannels.isChannel(name) ? SocketChannels.MESSAGE_TYPES[name] : null;
	}

	/**
	 * The channel names in a handshake value (`user,match`) or a setChannels
	 * list (['user', 'match']), deduplicated in the order given. Returns null
	 * when the value is neither or names a channel that does not exist.
	 */
	static parse(value) {
		let names = null;
		if(typeof value === 'string') {
			names = value.split(',').map((name) => name.trim()).filter((name) => name !== '');
		}
		else if(Array.isArray(value)) {
			names = value;
		}
		else {
			return null;
		}
		const channels = new Set();
		for(const name of names) {
			if(typeof name !== 'string' || !SocketChannels.isChannel(name)) {
				return null;
			}
			channels.add(name);
		}
		return channels;
	}

	/** The message type tags (as strings, the keys SocketActivity uses) a channel set receives. */
	static messageTypesOf(channels) {
		return new Set(Array.from(channels, (name) => String(SocketChannels.messageTypeOf(name))));
	}
}
